import { customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_CONTENT_FILTERING_BY_GRID } from '../../../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import { Column, ColumnKeys, getGridColumnDetails, GRID_COLUMNS } from './grid-config';
import { GzExchangeContentControlContentFilteringByGridDataProvider } from './GzExchangeContentControlContentFilteringByGridDataProvider';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../../../../ui/gzForm/formField/types/form-validator';
import { MAX_LENGTH, validateDuplicates, validateExpressionByType } from './utils/validators';
import { GRID_ITEM_FORM_ID } from '../../../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { GridCellEditWebComponent } from '../../../../../../../../../../grid/uiElements/gridCellEdit';
import {
  ExchangeContentControlContentFilteringRuleSettingsFilterByExpression,
  ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType,
} from '@nirvana/shared/dtos/types/policies/common';
import { CustomizableValidator } from '../../../../../../../../../../ui/gzForm/validators/customizable.validator';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { MaxLengthValidator } from '../../../../../../../../../../ui/gzForm/validators/max-length.validator';
import { ListEvents } from '../../../../../../../../../../ui/gzSingleSelectList/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_CONTENT_FILTERING_BY_GRID)
export class GzExchangeContentControlContentFilteringByGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ExchangeContentControlContentFilteringRuleSettingsFilterByExpression,
  ExchangeContentControlContentFilteringRuleSettingsFilterByExpression,
  GzExchangeContentControlContentFilteringByGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected translations = {
    editRow: translateFn('Edit'),
    deleteRow: translateFn('Delete'),
    emptyState: translateFn('No filtering rules defined'),
  };

  protected getDataProvider(): GzExchangeContentControlContentFilteringByGridDataProvider {
    return new GzExchangeContentControlContentFilteringByGridDataProvider(this.initialGridItems);
  }

  protected renderGridItemForm(): TemplateResult {
    return html` <gz-policy-panel-exchange-content-control-content-filtering-by-item
      id="${GRID_ITEM_FORM_ID}"
    ></gz-policy-panel-exchange-content-control-content-filtering-by-item>`;
  }

  protected validateGridItemBeforeAdd(
    gridItemFormValue: ExchangeContentControlContentFilteringRuleSettingsFilterByExpression
  ): ValidationResult {
    // check for duplicates
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzExchangeContentControlContentFilteringByGridDataProvider).items
    );
  }

  protected onEditRowEnabled(event: CustomEvent) {
    // set validators on edited row
    const { rowData } = event.detail;

    // get fields, because are interconnections between them
    let typeFormField, textFormField;
    this.gridComponent.controller?.view
      .getAllEditedCellsInRow(rowData.index + 2)
      .forEach((gridCellEdit: GridCellEditWebComponent) => {
        const cellColumnKey = gridCellEdit.getAttribute('id');

        if (cellColumnKey === ColumnKeys.TYPE) {
          typeFormField = gridCellEdit.formField;
        }

        if (cellColumnKey === ColumnKeys.TEXT) {
          textFormField = gridCellEdit.formField;
        }
      });

    // when selection change, validate text field
    typeFormField.addEventListener(ListEvents.SELECTION_CHANGE, () => {
      textFormField.validate(true);
    });

    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.TYPE, [
      RequiredValidator(translateFn('Required field')),
    ]);

    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.TEXT, [
      RequiredValidator(translateFn('Required field')),
      MaxLengthValidator(
        MAX_LENGTH,
        translateFn('The expression must have a maximum of {maxLength} characters.', { maxLength: MAX_LENGTH })
      ),
      CustomizableValidator(value =>
        validateExpressionByType(
          // use form field value, because it can be changed by user
          typeFormField.value as ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType,
          value
        )
      ),
    ]);
  }

  protected isEditedRowChanged(
    oldItem: ExchangeContentControlContentFilteringRuleSettingsFilterByExpression,
    newItem: ExchangeContentControlContentFilteringRuleSettingsFilterByExpression
  ): boolean {
    return (
      oldItem.text !== newItem.text ||
      oldItem.type !== newItem.type ||
      oldItem.matchCase !== newItem.matchCase ||
      oldItem.wholeWord !== newItem.wholeWord
    );
  }

  protected validateGridItemBeforeEdit(
    oldItem: ExchangeContentControlContentFilteringRuleSettingsFilterByExpression,
    newItem: ExchangeContentControlContentFilteringRuleSettingsFilterByExpression
  ): ValidationResult {
    // skip if entity is not changed
    if (
      newItem.text === oldItem.text &&
      newItem.type === oldItem.type &&
      newItem.matchCase === oldItem.matchCase &&
      newItem.wholeWord === oldItem.wholeWord
    ) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected revertGridCellEditValue(
    gridCellEdit: GridCellEditWebComponent,
    oldItem: ExchangeContentControlContentFilteringRuleSettingsFilterByExpression
  ): void {
    if (gridCellEdit.id === ColumnKeys.TEXT) {
      gridCellEdit.value = oldItem.text;
    }

    if (gridCellEdit.id === ColumnKeys.TYPE) {
      gridCellEdit.value = oldItem.type;
    }

    if (gridCellEdit.id === ColumnKeys.MATCH_CASE) {
      gridCellEdit.value = oldItem.matchCase;
    }

    if (gridCellEdit.id === ColumnKeys.WHOLE_WORD) {
      gridCellEdit.value = oldItem.wholeWord;
    }
  }
}
